import { Box, Button, Divider, Drawer, Stack, Switch, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { usePrivatePolicySettingsStore } from "../../usePrivatePolicySettingsStore/usePrivatePolicySettingsStore";
import { inverseNavyButton, navyButton } from "../../../../../styles/mui-overrides";
import LocationSelector from "../../../../../shared/LocationSelector";
import { PrivatePolicyComplianceConfig } from "../../../../../utilities/generated/gql-types";
import InternalEnforcementOptions from "./InternalEnforcementOptions";
import FmsEnforcementOptions from "./FmsEnforcementOptions";
import PpeNewOptions from "./PpeNewOptions";
import ConfirmDialog from "../../../../../shared/ConfirmDialog";
import dayjs from "dayjs";

type PrivatePolicyComplianceDrawerMenuProps = {
  handleClose: () => void;
  handleApply: () => void;
  relationshipId: string; // used for location selector
  privatePolicyConfigs: PrivatePolicyComplianceConfig[]; // the list of private policy configs for the selected relationship. Used to populate the title of the drawer based on which location is selected
};

function PrivatePolicyComplianceDrawerMenu({
  handleClose,
  handleApply,
  relationshipId,
  privatePolicyConfigs,
}: PrivatePolicyComplianceDrawerMenuProps) {
  const {
    configMenuOpen,
    internalEnforcementEnabled,
    setInternalEnforcementEnabled,
    fmsEnforcementEnabled,
    setFmsEnforcementEnabled,
    selectedLocationId,
    setSelectedLocationId,
    firstChargeNextBillingCycle,
    setFirstChargeNextBillingCycle,
    tenantPolicyExpirationRemindersEnabled,
    setTenantPolicyExpirationRemindersEnabled,
    ppeNewEnabled,
    setPpeNewEnabled,
    selectedPpeNewDate,
    setSelectedPpeNewDate,
    disablePPExpiry,
    setDisablePPExpiry,
    confirmationDialogOpen,
    setConfirmationDialogOpen,
  } = usePrivatePolicySettingsStore((state) => ({
    configMenuOpen: state.configMenuOpen,
    internalEnforcementEnabled: state.internalEnforcementEnabled,
    setInternalEnforcementEnabled: state.setInternalEnforcementEnabled,
    fmsEnforcementEnabled: state.fmsEnforcementEnabled,
    setFmsEnforcementEnabled: state.setFmsEnforcementEnabled,
    selectedLocationId: state.selectedLocationId,
    setSelectedLocationId: state.setSelectedLocationId,
    firstChargeNextBillingCycle: state.firstChargeNextBillingCycle,
    setFirstChargeNextBillingCycle: state.setFirstChargeNextBillingCycle,
    tenantPolicyExpirationRemindersEnabled: state.tenantPolicyExpirationRemindersEnabled,
    setTenantPolicyExpirationRemindersEnabled: state.setTenantPolicyExpirationRemindersEnabled,
    ppeNewEnabled: state.ppeNewEnabled,
    setPpeNewEnabled: state.setPpeNewEnabled,
    selectedPpeNewDate: state.selectedPpeNewDate,
    setSelectedPpeNewDate: state.setSelectedPpeNewDate,
    disablePPExpiry: state.disablePPExpiry,
    setDisablePPExpiry: state.setDisablePPExpiry,
    confirmationDialogOpen: state.confirmationDialogOpen,
    setConfirmationDialogOpen: state.setConfirmationDialogOpen,
  }));

  const isApplyDisabled = ppeNewEnabled && !selectedPpeNewDate;

  const getDisabledReason = () => {
    const reasons: string[] = [];

    if (ppeNewEnabled && !selectedPpeNewDate) {
      reasons.push("Please select a PPE New date");
    }

    if (reasons.length === 0) return "";
    if (reasons.length === 1) return reasons[0];
    return "Please address the following:\n" + reasons.map((reason) => `• ${reason}`).join("\n");
  };

  const handlePpeNewToggle = () => {
    if (ppeNewEnabled && selectedPpeNewDate && selectedPpeNewDate.startOf("day").isBefore(dayjs().startOf("day"))) {
      setConfirmationDialogOpen(true);
    } else {
      setPpeNewEnabled(!ppeNewEnabled);
    }
  };

  const handlePpeNewConfirm = () => {
    setPpeNewEnabled(false);
    setConfirmationDialogOpen(false);
  };

  const handlePpeNewCancel = () => {
    setConfirmationDialogOpen(false);
  };

  return (
    <Drawer
      anchor="right"
      open={configMenuOpen}
      onClose={handleClose}
      PaperProps={{
        sx: { width: "40%", minWidth: "450px", backgroundColor: "rgba(255, 255, 255, 1)" },
      }}
    >
      <Stack spacing={2} sx={{ padding: "32px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Stack>
            <Typography fontWeight="700" fontFamily="Open Sans, sans-serif" color="rgba(40, 121, 251, 1)">
              Private Policy Compliance Configuration
            </Typography>
            <Typography color="rgba(21, 39, 68, 1)" variant="h5">
              {privatePolicyConfigs.find((config) => config.locationId === selectedLocationId.toString())?.locationAddress ||
                "All Locations"}
            </Typography>
          </Stack>
          <Box sx={{ display: "flex" }}>
            <LocationSelector
              locationId={selectedLocationId}
              setLocationId={setSelectedLocationId}
              relationshipId={relationshipId}
              width="140px"
            />
          </Box>
        </Box>
        <Divider variant="middle" />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">FMS Private Policy Enforcement</Typography>
            <Switch checked={fmsEnforcementEnabled} onChange={() => setFmsEnforcementEnabled(!fmsEnforcementEnabled)} />
          </Stack>
          {fmsEnforcementEnabled && <FmsEnforcementOptions />}
        </Stack>
        <Divider variant="middle" />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Internal Private Policy Enforcement</Typography>
            <Switch checked={internalEnforcementEnabled} onChange={() => setInternalEnforcementEnabled(!internalEnforcementEnabled)} />
          </Stack>
          {internalEnforcementEnabled && <InternalEnforcementOptions />}
        </Stack>
        <Divider variant="middle" />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">PPE New</Typography>
            <Switch checked={ppeNewEnabled} onChange={handlePpeNewToggle} />
          </Stack>
          {ppeNewEnabled && <PpeNewOptions />}
        </Stack>
        <Divider variant="middle" />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">First Charge Next Billing Cycle</Typography>
              <Tooltip
                placement="right"
                title="When FMS or Internal enforcement goes live, non-compliant private policies be removed immediately and units will be enrolled effective the first day of the next billing cycle."
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Stack>
            <Switch checked={firstChargeNextBillingCycle} onChange={() => setFirstChargeNextBillingCycle(!firstChargeNextBillingCycle)} />
          </Stack>
        </Stack>
        <Divider variant="middle" />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Tenant Policy Expiration Reminders</Typography>
            <Switch
              checked={tenantPolicyExpirationRemindersEnabled}
              onChange={() => setTenantPolicyExpirationRemindersEnabled(!tenantPolicyExpirationRemindersEnabled)}
            />
          </Stack>
        </Stack>
        <Divider variant="middle" />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Disable Private Policy Expiry</Typography>
              <Tooltip
                placement="right"
                title="If enabled, accepted private policies uploaded to SafeLease will still count as valid even after the policy expiration date."
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Stack>
            <Switch checked={disablePPExpiry} onChange={() => setDisablePPExpiry(!disablePPExpiry)} />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2} paddingTop="50px" justifyContent="flex-end">
          <Button disableElevation sx={{ ...inverseNavyButton, width: "224px" }} variant="contained" onClick={handleClose}>
            Cancel
          </Button>
          <Tooltip title={getDisabledReason()} placement="top">
            <span>
              <Button
                disableElevation
                sx={{ ...navyButton, width: "224px" }}
                variant="contained"
                onClick={handleApply}
                disabled={isApplyDisabled}
              >
                Apply
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={confirmationDialogOpen}
        title="Disable PPE New"
        description="Are you sure you want to disable PPE New? This will clear the PPE New date and cannot be easily re-enabled if the date is in the past."
        onClose={handlePpeNewCancel}
        onConfirm={handlePpeNewConfirm}
      />
    </Drawer>
  );
}

export { PrivatePolicyComplianceDrawerMenu };
